import { useState } from 'react';

import { ReplacementDetail } from './ReplacementDetail';
import { ReplacementList } from './ReplacementList';
import styles from './ReplacementScreen.module.css';

/**
 * The replacements screen (M-13, Task 5, spec §5) — `/replacements`, a nav
 * entry that did not exist here at all before this milestone. The legacy's
 * own two links are COMMENTED OUT (`includes/left-sidebar.php:161,166`) and
 * no other sidebar mentions replacements anywhere, so every page in that
 * module worked only if somebody typed the URL. Commenting out a link is
 * not a decision to retire a feature; it is the absence of one (spec §0).
 *
 * ## The list is never unmounted to show a detail
 *
 * The identical shape `JustificationScreen.tsx`'s and
 * `EscalationScreen.tsx`'s own docblocks explain at length: an early return
 * that swaps `<ReplacementList>` for `<ReplacementDetail>` destroys the
 * list's own `draftFilters`/`appliedFilters`/`cursor` state the moment a row
 * opens, so `onBack` would come back to a freshly-mounted, unfiltered list.
 * The list stays mounted in a `hidden` div; opening a row only adds
 * `<ReplacementDetail>` as a sibling.
 *
 * ## The `hidden` div carries NO `className` (M-10's C-2, restated here)
 *
 * An author-origin `display` declaration beats the UA stylesheet's
 * `[hidden] { display: none }` — cascade ORIGIN wins before specificity is
 * even considered. `EscalationScreen.tsx` shipped exactly that defect once
 * (`className={styles['listPage']}` on the same element as `hidden`). A
 * BARE `hidden` div, with the list's own layout on a nested div inside it.
 *
 * ## Acting on a row refreshes the list behind it
 *
 * A dispense or a reject moves the row out of the default `requested`
 * queue. `onChanged` bumps a key the list watches, so returning from the
 * detail does not show a stale row still sitting in a queue it has left —
 * the list is refetched rather than re-mounted, so its filters survive.
 */

export interface ReplacementScreenProps {
  permissions: readonly string[];
}

export function ReplacementScreen({ permissions }: ReplacementScreenProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div className={styles['page']}>
      {selected === null ? null : (
        <ReplacementDetail
          uuid={selected}
          onBack={() => {
            setSelected(null);
          }}
          onChanged={() => {
            setRefreshKey((current) => current + 1);
          }}
        />
      )}

      {/* `hidden`, never a ternary that unmounts — see this file's own
          docblock. Bare — no `className` here; the list's own layout lives
          inside `ReplacementList` itself, not on this element (C-2). */}
      <div hidden={selected !== null}>
        <ReplacementList
          permissions={permissions}
          active={selected === null}
          refreshKey={refreshKey}
          onOpen={(uuid) => {
            setSelected(uuid);
          }}
        />
      </div>
    </div>
  );
}
